import React from "react";
//#region types
import { TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
import { clarificationOf } from "./agentMarkdown";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { RadioSelectionGroup } from "components/common";
import { TextAreaInput, VerticalFormField, WintForm, useWintForm } from "components/common/wintForms";
import AgentActions from "./agentActions";
import AgentAnswerText from "./agentAnswerText";
import AgentPanel from "./agentPanel";
import useCurrentAgentConversation from "./useCurrentAgentConversation";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

interface AgentClarificationProps {
  /** The agent's question, Markdown. */
  text: string;
  onAnswer: (text: string, fromChoice: boolean) => void;
  /** "Ingen av dessa" with nothing written, or she skips the question: the case goes to support. */
  onSkip: (reason: "none" | "skipped") => void;
}

const NoneOfTheseId = "none";
const AnswerMinRows = 2;
const t = LocalizationHelperInstance.getFormattedMessage;

const AgentClarification: React.FC<AgentClarificationProps> = ({ text, onAnswer, onSkip }) => {
  const { lead, choices, footnote } = React.useMemo(() => clarificationOf(text), [text]);
  const [selected, setSelected] = React.useState<string>();
  const { conversation } = useCurrentAgentConversation();
  const { wintFormProps, connect, values } = useWintForm<{ answer: string }>({ defaultValues: { answer: "" } });

  const items: SelectionItem[] = [
    ...choices.map((choice, index) => ({ id: String(index), value: choice.title, description: choice.description })),
    {
      id: NoneOfTheseId,
      value: t(TranslationKeys.SupportIssue.AgentNoneOfThese),
      description: t(TranslationKeys.SupportIssue.AgentNoneOfTheseHint),
    },
  ];

  const written = values.answer?.trim() ?? "";
  const writesOwn = !choices.length || selected === NoneOfTheseId;
  const canConfirm = choices.length ? selected !== undefined : !!written;

  // The pick is tracked as its position, never its text: the choices are written from the customer's data.
  // A string position keeps "none" and an index in one Sentry attribute; Sentry types each attribute by its value.
  const select = (value: string) => {
    setSelected(value);
    conversation.track("clarification.picked", {
      choice: value === NoneOfTheseId ? "none" : value,
      choices: choices.length,
    });
  };

  const confirm = () => {
    if (!choices.length) onAnswer(written, false);
    else if (selected === NoneOfTheseId) {
      if (written) onAnswer(written, false);
      else onSkip("none");
    } else if (selected !== undefined) onAnswer(choices[Number(selected)].text, true);
  };

  const answerField = (
    <Grid container className={cssExports.agentAskForm}>
      <WintForm {...wintFormProps}>
        <VerticalFormField<TextInputProps>
          Component={TextAreaInput}
          {...connect("answer")}
          label={t(
            choices.length
              ? TranslationKeys.SupportIssue.AgentNoneOfTheseDetails
              : TranslationKeys.SupportIssue.AgentClarifyPlaceholder
          )}
          minRows={AnswerMinRows}
        />
      </WintForm>
    </Grid>
  );

  return (
    <>
      <AgentPanel
        label={<AgentAnswerText text={lead} />}
        footnote={footnote ? <AgentAnswerText text={footnote} /> : undefined}
      >
        {choices.length ? (
          <Grid container className={cssExports.agentChoiceList}>
            <RadioSelectionGroup items={items} value={selected ?? ""} onChange={(_, value) => select(value)} />
          </Grid>
        ) : null}
        {writesOwn ? answerField : null}
      </AgentPanel>
      <AgentActions
        secondary={{
          labelKey: TranslationKeys.SupportIssue.AgentSkip,
          onClick: () => onSkip("skipped"),
          testId: "agent-clarify-skip",
        }}
        primary={{
          labelKey: TranslationKeys.SupportIssue.AgentConfirm,
          onClick: confirm,
          testId: "agent-clarify-confirm",
          disabled: !canConfirm,
        }}
      />
    </>
  );
};

export default AgentClarification;
